import { useState, type FormEvent, type JSX } from 'react';
import { Link } from 'react-router';
import { authApi } from '../api/client.js';
import { useAuth } from '../auth/context.js';
import { useI18n } from '../i18n/context.js';
import { Select } from '../components/Select.js';
import type { Locale } from '../i18n/registry.js';
import { PhotoConsent } from '../library/PhotoConsent.js';
import { DeleteAccount } from '../settings/DeleteAccount.js';

export function SettingsPage(): JSX.Element {
  const { user, setUser } = useAuth();
  const { locale, setLocale, available, t } = useI18n();
  const [savingLocale, setSavingLocale] = useState(false);
  const [localeSaved, setLocaleSaved] = useState(false);
  const [localeError, setLocaleError] = useState(false);

  const changeLocale = (next: Locale): void => {
    const previous = locale;
    setLocale(next);
    setLocaleSaved(false);
    setLocaleError(false);
    setSavingLocale(true);

    void authApi
      .updateLocale(next)
      .then(() => {
        if (user) setUser({ ...user, locale: next });
        setLocaleSaved(true);
      })
      .catch(() => {
        setLocale(previous);
        setLocaleError(true);
      })
      .finally(() => setSavingLocale(false));
  };

  return (
    <section className="mx-auto max-w-3xl">
      <h1 className="text-2xl font-semibold">{t.settings.title}</h1>

      <div className="mt-8 grid gap-5">
        <Profile />

        {/* A brand with a single language has nothing to choose. */}
        {available.length > 1 ? (
          <section className="kitup-card grid gap-4 p-5" aria-labelledby="language-title">
            <div>
              <h2 id="language-title" className="font-semibold">
                {t.settings.language.title}
              </h2>
              <p className="mt-1 text-sm text-[var(--kitup-color-muted)]">
                {t.settings.language.description}
              </p>
            </div>
            <label className="grid max-w-sm gap-1.5 text-sm font-medium">
              {t.settings.language.label}
              {/* Список языков — тот, что объявил бренд, а не все, что есть в коде. */}
              <Select
                value={locale}
                disabled={savingLocale}
                options={available.map((option) => ({
                  value: option,
                  label: t.settings.language.options[option],
                }))}
                onChange={changeLocale}
              />
            </label>
            {localeSaved ? (
              <p role="status" className="text-sm text-[var(--kitup-color-muted)]">
                {t.settings.language.saved}
              </p>
            ) : null}
            {localeError ? (
              <p role="alert" className="text-sm text-red-700">
                {t.common.unexpectedError}
              </p>
            ) : null}
          </section>
        ) : null}

        <section className="kitup-card p-5" aria-labelledby="billing-title">
          <h2 id="billing-title" className="font-semibold">
            {t.settings.billing.title}
          </h2>
          <p className="mt-1 text-sm text-[var(--kitup-color-muted)]">
            {t.settings.billing.description}
          </p>
          <Link to="/app" className="kitup-secondary-button mt-4">
            {t.books.title}
          </Link>
        </section>

        <section className="kitup-card grid gap-3 p-5" aria-labelledby="photo-consent-title">
          <h2 id="photo-consent-title" className="font-semibold">
            {t.library.consent.settingsTitle}
          </h2>
          <PhotoConsent settings />
        </section>

        <DeleteAccount />
      </div>
    </section>
  );
}

/**
 * Name, email and password: the account had no way to fix a mistyped name or to change a
 * password other than "forgot password" from the sign-in page.
 */
function Profile(): JSX.Element {
  const { user, setUser } = useAuth();
  const { t } = useI18n();
  const [name, setName] = useState(user?.name ?? '');
  const [state, setState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');

  if (!user) return <></>;

  const save = (event: FormEvent): void => {
    event.preventDefault();
    const next = name.trim();
    if (!next || next === user.name) return;
    setState('saving');
    void authApi
      .updateName(next)
      .then((result) => {
        setUser({ ...user, name: result.name });
        setState('saved');
      })
      .catch(() => setState('error'));
  };

  return (
    <section className="kitup-card grid gap-4 p-5" aria-labelledby="profile-title">
      <h2 id="profile-title" className="font-semibold">
        {t.settings.profile.title}
      </h2>
      <form onSubmit={save} className="flex flex-wrap items-end gap-3">
        <label className="grid min-w-0 flex-1 basis-60 gap-1.5 text-sm font-medium">
          {t.common.name}
          <input
            className="kitup-input"
            value={name}
            maxLength={80}
            autoComplete="name"
            onChange={(event) => {
              setName(event.target.value);
              setState('idle');
            }}
          />
        </label>
        <button
          type="submit"
          className="kitup-secondary-button"
          disabled={state === 'saving' || !name.trim() || name.trim() === user.name}
        >
          {state === 'saving' ? t.common.saving : t.library.actions.save}
        </button>
      </form>
      {state === 'saved' ? (
        <p role="status" className="text-sm text-emerald-700">
          {t.common.saved}
        </p>
      ) : null}
      {state === 'error' ? (
        <p role="alert" className="text-sm text-red-700">
          {t.common.unexpectedError}
        </p>
      ) : null}
      <dl className="grid gap-1 text-sm">
        <dt className="font-medium">{t.common.email}</dt>
        <dd className="text-[var(--kitup-color-muted)]">{user.email}</dd>
      </dl>
      <p className="text-sm">
        <Link
          to="/forgot-password"
          className="text-[var(--kitup-color-accent)] underline underline-offset-2"
        >
          {t.settings.profile.changePassword}
        </Link>{' '}
        <span className="text-[var(--kitup-color-muted)]">
          {t.settings.profile.changePasswordHint}
        </span>
      </p>
    </section>
  );
}
